import React from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import {
  useScopedTunnels,
  useServerName,
} from "../TroubleshootingUtils/useScope";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  GapResult,
  PortCheckResponse,
  ProbeOrigin,
} from "../troubleshootingInterfaces";
import {
  Card,
  CardHeader,
  GapNotice,
  Hint,
  Mono,
  Pill,
  TsButton,
} from "../TroubleshootingUi";
import { runPortCheck } from "../TroubleshootingUtils/troubleshootingApi";
import { useTargetCheck } from "../TargetHostField";
import { tunnelMenuItems } from "./tunnelMenu";
import { siteFamily } from "../TroubleshootingUtils/siteFamily";
import { formatRelative } from "../TroubleshootingUtils/formatters";
import {
  PORT_CHECK_COOLDOWN_SECONDS,
  useCooldown,
  waitLabel,
} from "../TroubleshootingUtils/useCooldown";

/** Same name the Connectivity analyzer and admin's results use. */
const DEFAULT_PATH_LABEL = "Default path";

/**
 * Milestone sites send alarms straight from the connector to the site's
 * Milestone event server (a TCP connection to `event_server_ip:alert_port`
 * per alarm), not through an Immix/Dice receiver, so they have no row in
 * the receiver table. This checks that path: can we open that connection
 * right now. It sends nothing to Milestone; a real test event has to come
 * from the connector and is not built yet.
 */
export const MilestoneEventServerCard = () => {
  const { scope, siteDetail, sampleData } = useTroubleshootingStore();
  const [running, setRunning] = React.useState(false);
  const [result, setResult] =
    React.useState<GapResult<PortCheckResponse> | null>(null);
  const cooldown = useCooldown();
  // The site's own tunnels are labelled; every tunnel the user can see is
  // offered, because the one carrying the event server may be another
  // group's (cross-group routing).
  const siteTunnels = useScopedTunnels();
  const tunnels = useTroubleshootingStore((s) => s.tunnels);
  const getServerName = useServerName();
  // Default path unless the user picks a tunnel; never guessed. The default
  // path does not reach WireGuard networks, so a WireGuard event server
  // needs its tunnel picked here.
  const [from, setFrom] = React.useState<string>("nat");

  const targetCheck = useTargetCheck(
    (siteDetail?.event_server_ip || "").trim(),
    from.startsWith("wg:")
      ? { kind: "wireguard", tunnel_id: Number(from.slice(3)) }
      : { kind: "nat" }
  );

  // A new site starts with no result rather than the last site's.
  React.useEffect(() => {
    setResult(null);
  }, [siteDetail?.id]);

  if (
    scope !== "site" ||
    !siteDetail ||
    siteFamily(siteDetail.integration_type_name) !== "milestone"
  )
    return null;

  const host = (siteDetail.event_server_ip || "").trim();
  const port =
    typeof siteDetail.alert_port === "number" && siteDetail.alert_port > 0
      ? siteDetail.alert_port
      : null;

  const picked = from.startsWith("wg:")
    ? tunnels.find((tunnel) => tunnel.id === Number(from.slice(3)))
    : undefined;
  const origin: ProbeOrigin = picked
    ? { kind: "wireguard", tunnel_id: picked.id }
    : { kind: "nat" };
  const originLabel = picked
    ? `WireGuard gateway ${getServerName(picked.server ?? null)} via ${
        picked.name || "tunnel"
      }`
    : DEFAULT_PATH_LABEL;

  const check = async () => {
    if (!host || port == null || !targetCheck.allowed) return;
    setRunning(true);
    const response = await runPortCheck(
      { host, ports: [port], from: origin },
      originLabel,
      sampleData
    );
    setRunning(false);
    setResult(response);
    cooldown.start(
      response.status === "error" && response.retryAfterSeconds
        ? response.retryAfterSeconds
        : PORT_CHECK_COOLDOWN_SECONDS
    );
  };

  const data = result?.status === "ok" ? result.data : null;
  const outcome = data?.results.find((r) => r.port === port) ?? null;

  return (
    <Card testId="milestone-event-server-card">
      <CardHeader
        title="Milestone event server"
        hint="This site sends its alarms to its own Milestone event server: the connector opens a connection to the alert port for each alarm. Check path tests that connection now. It sends nothing to Milestone."
        right={
          <TsButton
            size="sm"
            variant="outline"
            onClick={check}
            disabled={
              running ||
              cooldown.remaining > 0 ||
              !host ||
              port == null ||
              !targetCheck.allowed
            }
            data-testid="milestone-check-path"
          >
            {running
              ? "Checking…"
              : cooldown.remaining > 0
              ? waitLabel(cooldown.remaining)
              : "Check path"}
          </TsButton>
        }
      />
      <Box
        sx={{
          px: 2.5,
          pb: 2.25,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Hint>
          {host ? <Mono>{host}</Mono> : "No event server address saved"}
          {" · "}
          {port != null ? (
            <>
              alert port <Mono>{port}</Mono>
            </>
          ) : (
            "no alert port saved"
          )}
          {host && port != null ? ` · tested from ${originLabel}` : ""}
        </Hint>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            component="label"
            htmlFor="ts-ms-from"
            sx={{ ...MULISH, fontSize: 13, fontWeight: 700 }}
          >
            Test from
          </Typography>
          <Select
            id="ts-ms-from"
            size="small"
            value={from}
            onChange={(e) => setFrom(String(e.target.value))}
            sx={{
              ...MULISH,
              fontSize: 14,
              minWidth: 260,
              backgroundColor: "#fff",
            }}
          >
            <MenuItem value="nat" sx={MULISH}>
              {DEFAULT_PATH_LABEL}
            </MenuItem>
            {tunnelMenuItems(
              tunnels,
              (siteDetail?.event_server_ip || "").trim(),
              new Set(siteTunnels.map((tunnel) => tunnel.id))
            )}
          </Select>
          <Hint>
            The default path reaches public and IPsec addresses. For an event
            server behind WireGuard, pick its tunnel.
          </Hint>
        </Box>
        {host && !targetCheck.allowed && targetCheck.message && (
          <Box data-testid="milestone-not-allowed">
            <Hint sx={{ color: "#B91C1C" }}>{targetCheck.message}</Hint>
          </Box>
        )}
        {(!host || port == null) && (
          <Hint>
            Nothing to check until the site&rsquo;s event server address and
            alert port are saved on its page.
          </Hint>
        )}
        {result && result.status !== "ok" && (
          <GapNotice
            result={result}
            what="The event server check"
            kind="check"
          />
        )}
        {data && outcome && (
          <Box data-testid="milestone-check-result">
            {outcome.result === "open" ? (
              <>
                <Pill tone="ok">Listening</Pill> The event server accepted a
                connection on port {port} from {data.from_label}
                {outcome.ms != null ? ` in ${outcome.ms} ms` : ""}. The path our
                alarms take is open.
              </>
            ) : outcome.result === "closed" ? (
              <>
                <Pill tone="orange">Refused</Pill> The server answered but
                nothing is listening on port {port}. Check the port set for
                Actuate&rsquo;s analytics events in Milestone, and that the
                Event Server service is running.
              </>
            ) : outcome.result === "filtered" ? (
              <>
                <Pill tone="orange">No reply</Pill> Nothing answered on port{" "}
                {port} within the probe window. Usually a firewall rule or a
                missing route between {data.from_label} and the event server.
              </>
            ) : (
              <>
                <Pill tone="grey">Could not check</Pill>{" "}
                {outcome.detail || "The probe hit an error."} Try again in a
                moment.
              </>
            )}
            <Hint sx={{ display: "block", mt: 0.5 }}>
              Checked {formatRelative(data.checked_at)}.
            </Hint>
          </Box>
        )}
      </Box>
    </Card>
  );
};
